import React, { useState } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Collapse,
  IconButton,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Divider,
  useTheme,
} from '@mui/material';
import {
  CheckCircle as CheckIcon,
  Warning as WarningIcon,
  Error as ErrorIcon,
  Info as InfoIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  Lightbulb as LightbulbIcon,
  AutoFixHigh as AutoFixIcon,
} from '@mui/icons-material';
import { AreaResult, Finding } from '../../services/customsAuditService';

interface AreaDetailCardProps {
  result: AreaResult;
  defaultExpanded?: boolean;
  onApplyFix?: (field: string, value: string) => void;
}

const AREA_LABELS: Record<string, string> = {
  description: 'Goods Description',
  tariff: 'Tariff Classification',
  price: 'Customs Value / Price',
  origin: 'Origin Verification',
  export_control: 'Export Control & Sanctions',
  physical: 'Physical / Weight Validation',
  tampering: 'Document Tampering',
};

const AREA_ICONS: Record<string, string> = {
  description: 'Description',
  tariff: 'Category',
  price: 'AttachMoney',
  origin: 'Public',
  export_control: 'Shield',
  physical: 'FitnessCenter',
  tampering: 'FindInPage',
};

const getSeverityIcon = (severity: string) => {
  switch (severity) {
    case 'critical': return <ErrorIcon color="error" fontSize="small" />;
    case 'high': return <WarningIcon color="error" fontSize="small" />;
    case 'medium': return <WarningIcon color="warning" fontSize="small" />;
    case 'low': return <InfoIcon color="info" fontSize="small" />;
    default: return <InfoIcon color="action" fontSize="small" />;
  }
};

const getSeverityColor = (severity: string): 'error' | 'warning' | 'info' | 'default' => {
  switch (severity) {
    case 'critical': return 'error';
    case 'high': return 'error';
    case 'medium': return 'warning';
    case 'low': return 'info';
    default: return 'default';
  }
};

const getStatusChipColor = (status: string): 'success' | 'warning' | 'error' | 'default' => {
  switch (status) {
    case 'pass': return 'success';
    case 'warning': return 'warning';
    case 'fail': return 'error';
    default: return 'default';
  }
};

const AreaDetailCard: React.FC<AreaDetailCardProps> = ({ result, defaultExpanded = false, onApplyFix }) => {
  const [expanded, setExpanded] = useState(defaultExpanded || result.status !== 'pass');
  const theme = useTheme();

  const borderColor = result.status === 'pass'
    ? theme.palette.success.main
    : result.status === 'warning'
    ? theme.palette.warning.main
    : theme.palette.error.main;

  return (
    <Paper
      sx={{
        mb: 2,
        borderLeft: `4px solid ${borderColor}`,
        overflow: 'hidden',
      }}
    >
      {/* Header - always visible */}
      <Box
        sx={{
          p: 2,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          cursor: 'pointer',
          '&:hover': { bgcolor: 'action.hover' },
        }}
        onClick={() => setExpanded(!expanded)}
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Typography variant="h6" fontWeight={600}>
            {AREA_LABELS[result.area] || result.area}
          </Typography>
          <Chip
            label={result.status.toUpperCase()}
            color={getStatusChipColor(result.status)}
            size="small"
            sx={{ fontWeight: 'bold' }}
          />
          <Typography variant="body2" color="text.secondary">
            Score: {result.score}/100
          </Typography>
        </Box>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          {result.findings.length > 0 && (
            <Chip
              label={`${result.findings.length} finding${result.findings.length !== 1 ? 's' : ''}`}
              size="small"
              variant="outlined"
            />
          )}
          <IconButton size="small">
            {expanded ? <ExpandLessIcon /> : <ExpandMoreIcon />}
          </IconButton>
        </Box>
      </Box>

      {/* Expandable content */}
      <Collapse in={expanded}>
        <Divider />
        <Box sx={{ p: 2 }}>
          {/* Findings */}
          {result.findings.length > 0 ? (
            <>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Findings
              </Typography>
              <List dense disablePadding>
                {result.findings.map((finding: Finding, idx: number) => (
                  <ListItem key={idx} disableGutters sx={{ alignItems: 'flex-start' }}>
                    <ListItemIcon sx={{ minWidth: 32, mt: 0.5 }}>
                      {getSeverityIcon(finding.severity)}
                    </ListItemIcon>
                    <ListItemText
                      primary={
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                          <Chip
                            label={finding.severity.toUpperCase()}
                            color={getSeverityColor(finding.severity)}
                            size="small"
                            sx={{ fontSize: '0.65rem', height: 20 }}
                          />
                          <Typography variant="body2">{finding.message}</Typography>
                        </Box>
                      }
                      secondary={
                        <Box component="span">
                          {(finding.field || finding.expected || finding.actual) && (
                            <Typography variant="caption" color="text.secondary" component="span" display="block">
                              {finding.field && `Field: ${finding.field}`}
                              {finding.expected && ` | Expected: ${finding.expected}`}
                              {finding.actual && ` | Actual: ${finding.actual}`}
                            </Typography>
                          )}
                          {finding.suggested_fix && onApplyFix && finding.field && (
                            <Chip
                              icon={<AutoFixIcon sx={{ fontSize: 14 }} />}
                              label={`Fix \u2192 ${finding.suggested_fix.length > 40 ? finding.suggested_fix.substring(0, 40) + '...' : finding.suggested_fix}${finding.fix_confidence ? ` (${Math.round(finding.fix_confidence * 100)}%)` : ''}`}
                              size="small"
                              color="primary"
                              variant="outlined"
                              onClick={(e) => {
                                e.stopPropagation();
                                onApplyFix(finding.field!, finding.suggested_fix!);
                              }}
                              sx={{ mt: 0.5, cursor: 'pointer', fontSize: '0.7rem', height: 24 }}
                            />
                          )}
                        </Box>
                      }
                    />
                  </ListItem>
                ))}
              </List>
            </>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No issues found in this area.
            </Typography>
          )}

          {/* Recommendations */}
          {result.recommendations.length > 0 && (
            <Box sx={{ mt: 2 }}>
              <Typography variant="subtitle2" fontWeight={600} gutterBottom>
                Recommendations
              </Typography>
              <List dense disablePadding>
                {result.recommendations.map((rec, idx) => (
                  <ListItem key={idx} disableGutters>
                    <ListItemIcon sx={{ minWidth: 32 }}>
                      <LightbulbIcon color="info" fontSize="small" />
                    </ListItemIcon>
                    <ListItemText primary={rec} primaryTypographyProps={{ variant: 'body2' }} />
                  </ListItem>
                ))}
              </List>
            </Box>
          )}
        </Box>
      </Collapse>
    </Paper>
  );
};

export default AreaDetailCard;
